// Airspace vertical limits and route conflict checks.

import type { LngLat } from './geo';
import type { Airspace, AirspaceLimit } from '../sources/types';

const FT = 0.3048;

/** Convert a limit to metres MSL. GND-referenced limits need the ground elevation there.
 * Flight levels are converted with the standard atmosphere (no QNH correction). */
export function limitToMsl(l: AirspaceLimit, ground: number): number {
  const v = l.unit === 'FL' ? l.value * 100 * FT : l.unit === 'ft' ? l.value * FT : l.value;
  return l.ref === 'GND' ? ground + v : v;
}

export function formatLimit(l: AirspaceLimit): string {
  if (l.ref === 'GND' && l.value === 0) return 'GND';
  if (l.unit === 'FL') return `FL${l.value}`;
  return `${l.value} ${l.unit} ${l.ref === 'GND' ? 'AGL' : 'MSL'}`;
}

export function pointInRing(p: LngLat, ring: LngLat[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export function pointInPolygon(p: LngLat, rings: LngLat[][]): boolean {
  if (!rings.length || !pointInRing(p, rings[0])) return false;
  for (let i = 1; i < rings.length; i++) if (pointInRing(p, rings[i])) return false;
  return true;
}

export interface RouteSample {
  d: number;
  at: LngLat;
  alt: number;
  ground: number;
}

export interface Crossing {
  airspace: Airspace;
  /** Distance range along the route where the route is under/over/inside the airspace footprint. */
  d0: number;
  d1: number;
  /** Per-sample floor and ceiling (MSL), NaN outside the footprint. */
  floor: number[];
  ceiling: number[];
  /** The route's altitude enters the volume somewhere. */
  conflict: boolean;
}

/** Airspaces whose footprint the route crosses, with whether the altitude enters the volume. */
export function routeCrossings(samples: RouteSample[], airspaces: Airspace[]): Crossing[] {
  const out: Crossing[] = [];
  for (const a of airspaces) {
    let any = false;
    let conflict = false;
    let d0 = Infinity;
    let d1 = -Infinity;
    const floor: number[] = [];
    const ceiling: number[] = [];
    for (const s of samples) {
      if (!pointInPolygon(s.at, a.polygon)) {
        floor.push(NaN);
        ceiling.push(NaN);
        continue;
      }
      any = true;
      d0 = Math.min(d0, s.d);
      d1 = Math.max(d1, s.d);
      const f = limitToMsl(a.floor, s.ground);
      const c = limitToMsl(a.ceiling, s.ground);
      floor.push(f);
      ceiling.push(c);
      if (s.alt > f && s.alt < c) conflict = true;
    }
    if (any) out.push({ airspace: a, d0, d1, floor, ceiling, conflict });
  }
  return out.sort((x, y) => Number(y.conflict) - Number(x.conflict) || x.d0 - y.d0);
}
